// ONDEMAND-1: RecordingDetail's transcribe-on-demand banner. Rendered only when the feature is
// enabled. Copy comes from the server's `TranscriptionStatus.message` wherever there is one
// ("Transcribing now — about 4 min.", a budget block's explanation) so web and API agree.
import { isDeferred, type Recording } from '@scribe/core/types';
import type { OpenTranscription } from '../../hooks/useOpenTranscription';

export function OnDemandBanner({
  recording,
  open,
}: {
  recording: Recording;
  open: OpenTranscription;
}) {
  const { result } = open;

  if (
    result?.state === 'budget_blocked' ||
    result?.state === 'failed' ||
    result?.state === 'unavailable'
  ) {
    return (
      <div className="banner banner-danger" role="alert">
        <p>{result.message}</p>
      </div>
    );
  }

  // Started (or already running): keep the server's ETA up until the transcript lands — the
  // recording's own refetch flips it to queued/transcribing, which also gets the usual
  // in-progress treatment below this banner.
  if (
    (result?.state === 'in_progress' || result?.state === 'processing') &&
    recording.status !== 'completed' &&
    recording.status !== 'failed'
  ) {
    return (
      <div className="banner banner-info" role="status">
        <p>{result.message}</p>
      </div>
    );
  }

  if (!isDeferred(recording)) return null;

  // Opening auto-ensures (useOpenTranscription), so until that settles this is "starting",
  // not "saved" — otherwise the Saved copy would flash for one frame on every open.
  const starting = open.pending || (!open.justRecorded && !result && !open.error);
  if (starting) {
    return (
      <div className="banner banner-info" role="status">
        <p>Starting transcription…</p>
      </div>
    );
  }

  return (
    <div className="banner banner-info">
      <p>
        Saved — not transcribed yet. It will be transcribed when opened, searched or asked about.
      </p>
      <button className="sm" onClick={open.transcribeNow}>
        Transcribe now
      </button>
      {open.error && (
        <p className="error">{open.error.message || 'Could not start transcription.'}</p>
      )}
    </div>
  );
}
